import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserTickets } from '../../store/slices/userSlice';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import UploadAvatar from './UploadAvatar';

const Profile = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { tickets, loading, error } = useSelector((state) => state.user);
  const { slug } = useParams();
  const [cliente, setCliente] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [toastMessage, setToastMessage] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [nombreSalon, setNombreSalon] = useState('');
  const [descSalon, setDescSalon] = useState('');
  const [country, setCountry] = useState('Argentina');
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [showProfileDetails, setShowProfileDetails] = useState(false);
  const [ciudadesOptions, setCiudadesOptions] = useState([]);


  const [searchTerm, setSearchTerm] = useState('');
  const [activeEventId, setActiveEventId] = useState('all');
  const [matchTickets, setMatchTickets] = useState([]);
  const [activeTab, setActiveTab] = useState('events'); // 'events' | 'sports'

  const groupedData = React.useMemo(() => {
    if (!Array.isArray(tickets)) return { all: [], events: {} };

    const events = {};
    const filtered = tickets.filter(t =>
      (t.event?.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.forEach(ticket => {
      const eventId = ticket.event?._id || 'unknown';
      if (!events[eventId]) {
        events[eventId] = {
          title: ticket.event?.title || 'Sin Evento',
          tickets: []
        };
      }
      events[eventId].tickets.push(ticket);
    });

    return { all: filtered, events };
  }, [tickets, searchTerm]);

  const currentTickets = activeEventId === 'all'
    ? groupedData.all
    : (groupedData.events[activeEventId]?.tickets || []);

  const isAdminHere = isAuthenticated && cliente && (
    (user?.role === 'admin' && user?.clientId && cliente?._id && user.clientId.toString() === cliente._id.toString()) || 
    (user?.memberships?.some(m => m.clientId && m.clientId.toString() === cliente._id.toString() && m.role === 'admin'))
  );

  // 🆕 estado del modal de QR
  const [qrAmpliado, setQrAmpliado] = useState(null); // { src, fileName }
  /*
  useEffect(() => {
    if (user && user.role !== 'admin') {
      dispatch(fetchUserTickets({ slug, userId: user.id }));
    axios.get('/api/match-tickets/my-tickets').then(res => setMatchTickets(res.data)).catch(err => console.error(err));
    }
  }, [dispatch, slug, user]);
  */
  useEffect(() => {
    if (!user) return;
    if (user.country) setCountry(user.country);
    if (user.province) setProvince(user.province);
    if (user.city) setCity(user.city);

    if (!cliente) return;
    // trae tickets del usuario para este salón
    dispatch(fetchUserTickets({ slug, userId: user.id }));
  }, [dispatch, slug, user, cliente]);

  useEffect(() => {
    if (country === 'Argentina' && province) {
      fetch(`https://apis.datos.gob.ar/georef/api/localidades?provincia=${province}&max=1000`)
        .then(res => res.json())
        .then(data => {
          if (data && data.localidades) {
            const names = data.localidades.map(l => l.nombre).sort();
            setCiudadesOptions([...new Set(names)]);
          }
        })
        .catch(err => console.error('Error fetching ciudades:', err));
    } else {
      setCiudadesOptions([]);
    }
  }, [country, province]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setToastMessage(location.state.successMessage);
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [location.state?.successMessage]);

  useEffect(() => {
    fetchCliente();
  }, [slug]);

  const fetchCliente = async () => {
    try {
      const res = await axios.get(`/api/clients/${slug}`);
      setCliente(res.data);
      setNombreSalon(res.data.nombre || '');
      setDescSalon(res.data.description || '');
      setCountry(res.data.country || 'Argentina');
      setProvince(res.data.province || '');
      setCity(res.data.city || '');
    } catch (err) {
      console.error('Error al obtener cliente:', err);
    } finally {
      setLoadingCliente(false);
    }
  };

  const handleUpdateProfile = async () => {
    setUpdating(true);
    try {
      // 1. Actualizar Salón (si es admin)
      if (isAdminHere) {
        await axios.put(`/api/clients/${cliente._id}`, {
          ...cliente,
          nombre: nombreSalon,
          description: descSalon,
          country,
          province,
          city
        });
      }

      // 2. Actualizar Usuario (siempre que esté logueado)
      await axios.put(`/api/auth/user/profile`, {
        country,
        province,
        city
      });

      setToastMessage('Perfil actualizado correctamente.');
      setTimeout(() => setToastMessage(null), 4000);
      fetchCliente();
      // Recargar datos del usuario en Redux si es necesario (opcional)
    } catch (err) {
      console.error('Error al actualizar perfil:', err);
      alert('No se pudo actualizar el perfil.');
    } finally {
      setUpdating(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await axios.get(`/mercadopago/connect/${slug}`);
      const { url } = response.data;
      window.location.href = url;
    } catch (error) {
      console.error('Error al iniciar la conexión con MercadoPago:', error);
      alert('No se pudo conectar con MercadoPago.');
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.put(`/api/clients/${cliente._id}`, {
        logoUrl: cliente.logoUrl,
        nombre: cliente.nombre,
        slug: cliente.slug,
        colores: {
          primario: cliente.colores?.primario || '#FFFFFF',
          secundario: cliente.colores?.secundario || '#000000',
        },
        accessToken: '',
        refreshToken: '',
        mpUserId: '',
        mpConnectedAt: null,
      });
      fetchCliente();
      setToastMessage('Cuenta de Mercado Pago desconectada correctamente.');
      setTimeout(() => setToastMessage(null), 4000);
    } catch (err) {
      console.error('Error al desconectar Mercado Pago:', err);
      alert('No se pudo desconectar la cuenta de MercadoPago.');
    }
  };

  const isMPConnected = Boolean(cliente?.hasMpConnected);

  // 🆕 cerrar modal con ESC
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setQrAmpliado(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 🆕 abrir modal con info de archivo
  const abrirQR = (ticket) => {
    const fecha = ticket.event?.date
      ? new Date(ticket.event.date).toISOString().slice(0, 10)
      : 'qr';
    const safeTitle = (ticket.event?.title || 'evento').replace(/[^a-z0-9-_]+/gi, '_');
    const address = ticket.event?.address
      ? `${ticket.event.address}, ${ticket.event.city || ''}`
      : ticket.event?.location;

    setQrAmpliado({
      src: ticket.qrImage,
      fileName: `qr_${safeTitle}_${fecha}.png`,
      locationQuery: address
    });
  };

  // 🆕 descargar QR (sirve para data URL o URL normal)
  const descargarQR = () => {
    if (!qrAmpliado?.src) return;
    const a = document.createElement('a');
    a.href = qrAmpliado.src;
    a.download = qrAmpliado.fileName || 'qr.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading) return <div>Cargando entradas...</div>;

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Error:</strong>{' '}
        {typeof error === 'object' ? error.message || JSON.stringify(error) : error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {toastMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-center">
          {toastMessage}
        </div>
      )}

      {isAdminHere && (
        <div className="mb-12">
          <div className="bg-surface rounded-3xl border border-white/5 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient"></div>
            
            {/* Cabecera de Perfil Compacta */}
            <div className="p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-brand-gradient p-0.5 shadow-lg shadow-orange-500/20">
                  <div className="w-full h-full bg-surface rounded-[14px] flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white mb-1">¡Hola, {user.name}!</h1>
                  <p className="text-white/40 text-sm font-medium">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowProfileDetails(!showProfileDetails)}
                  className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all border flex items-center gap-2 ${
                    showProfileDetails 
                    ? 'bg-white/10 border-white/20 text-white' 
                    : 'bg-transparent border-white/10 text-white/60 hover:border-white/20 hover:text-white'
                  }`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                  {showProfileDetails ? 'Cerrar Edición' : 'Editar Perfil'}
                </button>
                <Link
                  to={`/${slug}/admin/settings`}
                  className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 text-xs font-bold hover:bg-white/10 transition-all flex items-center gap-2"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                  Ajustes
                </Link>
              </div>
            </div>

            {/* Panel de Edición Colapsable */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showProfileDetails ? 'max-h-[800px] border-t border-white/5 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="p-6 md:p-8 bg-black/20">
                <div className="flex flex-col md:flex-row items-start lg:items-center gap-10">
                  <div className="flex-1 w-full">
                    <h3 className="text-sm font-bold text-white mb-6 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-orange"></span>
                      Información de Ubicación
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">País</label>
                        <select
                          value={country}
                          onChange={(e) => {
                            setCountry(e.target.value);
                            if (e.target.value !== 'Argentina') {
                              setProvince('');
                              setCity('');
                            }
                          }}
                          className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-primary-orange transition-all appearance-none"
                        >
                          {['Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Ecuador', 'España', 'Estados Unidos', 'México', 'Paraguay', 'Perú', 'Uruguay', 'Venezuela', 'Otro'].map(c => (
                            <option key={c} value={c} className="bg-[#080808]">{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Provincia</label>
                        {country === 'Argentina' ? (
                          <select
                            value={province}
                            onChange={(e) => {
                              setProvince(e.target.value);
                              setCity('');
                            }}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-primary-orange transition-all appearance-none"
                          >
                            <option value="" className="bg-[#080808]">Seleccionar...</option>
                            {[
                              'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes', 'Entre Ríos',
                              'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén', 'Río Negro',
                              'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
                            ].map(p => (
                              <option key={p} value={p} className="bg-[#080808]">{p}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={province}
                            onChange={(e) => setProvince(e.target.value)}
                            placeholder="Tu provincia/estado"
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-primary-orange transition-all"
                          />
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Ciudad</label>
                        <input
                          type="text"
                          list={country === 'Argentina' ? "ciudades-list-admin" : undefined}
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="Tu ciudad"
                          className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-primary-orange transition-all"
                          autoComplete="off"
                        />
                        {country === 'Argentina' && (
                          <datalist id="ciudades-list-admin">
                            {ciudadesOptions.map((c, i) => (
                              <option key={i} value={c} />
                            ))}
                          </datalist>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={handleUpdateProfile}
                      disabled={updating}
                      className="bg-brand-gradient text-white px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-orange-500/20 hover:opacity-90 transition-all active:scale-95 disabled:opacity-50"
                    >
                      {updating ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                  </div>
                  
                  <div className="w-full lg:w-auto p-6 bg-white/5 rounded-3xl border border-white/5">
                    <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mb-4">Foto de Perfil</p>
                    <UploadAvatar client={cliente} onUpdate={fetchCliente} />
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
                  <p className="text-[11px] text-white/30 font-medium">
                    La configuración del {cliente?.organizationType === 'sports' ? 'complejo' : 'salón'} se encuentra en ajustes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mis Datos (Para usuarios que no son admin del salón actual) */}
      {isAuthenticated && !isAdminHere && (
        <div className="mb-8">
          <div className="bg-surface rounded-3xl border border-white/5 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient opacity-30"></div>
            
            <div className="p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center border border-white/10">
                  <span className="text-xl">👤</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white mb-1">Tu Perfil: {user.name}</h1>
                  <p className="text-white/40 text-xs font-medium">{user.email}</p>
                </div>
              </div>

              <button
                onClick={() => setShowProfileDetails(!showProfileDetails)}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all border flex items-center gap-2 ${
                  showProfileDetails 
                  ? 'bg-white/10 border-white/20 text-white' 
                  : 'bg-transparent border-white/10 text-white/60 hover:border-white/20 hover:text-white'
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                {showProfileDetails ? 'Ocultar Datos' : 'Editar Mis Datos'}
              </button>
            </div>

            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showProfileDetails ? 'max-h-[500px] border-t border-white/5 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="p-6 md:p-8 bg-black/20">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">País</label>
                    <select
                      value={country}
                      onChange={(e) => {
                        setCountry(e.target.value);
                        if (e.target.value !== 'Argentina') {
                          setProvince('');
                          setCity('');
                        }
                      }}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-primary-orange transition-all appearance-none"
                    >
                      {['Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Ecuador', 'España', 'Estados Unidos', 'México', 'Paraguay', 'Perú', 'Uruguay', 'Venezuela', 'Otro'].map(c => (
                        <option key={c} value={c} className="bg-[#080808]">{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Provincia</label>
                    {country === 'Argentina' ? (
                      <select
                        value={province}
                        onChange={(e) => {
                          setProvince(e.target.value);
                          setCity('');
                        }}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-primary-orange transition-all appearance-none"
                      >
                        <option value="" className="bg-[#080808]">Seleccionar...</option>
                        {[
                          'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes', 'Entre Ríos',
                          'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén', 'Río Negro',
                          'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
                        ].map(p => (
                          <option key={p} value={p} className="bg-[#080808]">{p}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={province}
                        onChange={(e) => setProvince(e.target.value)}
                        placeholder="Provincia/Estado"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-primary-orange transition-all"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">Ciudad</label>
                    <input
                      type="text"
                      list={country === 'Argentina' ? "ciudades-list-user" : undefined}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Ciudad"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-primary-orange transition-all"
                      autoComplete="off"
                    />
                    {country === 'Argentina' && (
                      <datalist id="ciudades-list-user">
                        {ciudadesOptions.map((c, i) => (
                          <option key={i} value={c} />
                        ))}
                      </datalist>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleUpdateProfile}
                  disabled={updating}
                  className="bg-brand-gradient text-white px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-orange-500/20 hover:opacity-90 transition-all active:scale-95 disabled:opacity-50"
                >
                  {updating ? 'Guardando...' : 'Actualizar mis datos'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mis entradas */}
      {isAuthenticated && (
        <div className="mt-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">Mis Entradas</h2>
              <p className="text-white/40 text-sm font-medium">Gestioná tus accesos y códigos QR.</p>
            </div>

            <div className="relative group w-full md:w-80">
              <input
                type="text"
                placeholder="Buscar por evento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-surface border border-white/5 rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-white/20 focus:ring-2 focus:ring-primary-orange/50 focus:border-primary-orange transition-all outline-none"
              />
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-primary-orange transition-colors" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </div>
          </div>


          <div className="flex gap-4 mb-8 border-b border-white/10 pb-4">
            <button 
                onClick={() => setActiveTab('events')} 
                className={`text-lg font-black tracking-tight transition-colors ${activeTab === 'events' ? 'text-white' : 'text-white/40 hover:text-white'}`}
            >
                Entradas a Eventos
            </button>
            <button 
                onClick={() => setActiveTab('sports')} 
                className={`text-lg font-black tracking-tight transition-colors ${activeTab === 'sports' ? 'text-white' : 'text-white/40 hover:text-white'}`}
            >
                Entradas a Partidos
            </button>
          </div>

          {activeTab === 'events' ? (
            <>

          {/* Filtros por Evento (Solapas) */}
          {Object.keys(groupedData.events).length > 1 && (
            <div className="flex flex-wrap gap-2 mb-8 p-1.5 bg-surface/50 rounded-2xl border border-white/5 backdrop-blur-sm overflow-x-auto">
              <button
                onClick={() => setActiveEventId('all')}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-3 whitespace-nowrap ${activeEventId === 'all'
                  ? 'bg-white/10 text-white shadow-lg'
                  : 'text-white/40 hover:text-white hover:bg-white/5'
                  }`}
              >
                Todos
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-black ${activeEventId === 'all' ? 'bg-primary-orange text-white' : 'bg-white/5 text-white/30'}`}>
                  {tickets.length}
                </span>
              </button>
              {Object.keys(groupedData.events).map(id => (
                <button
                  key={id}
                  onClick={() => setActiveEventId(id)}
                  className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-3 whitespace-nowrap ${activeEventId === id
                    ? 'bg-white/10 text-white shadow-lg'
                    : 'text-white/40 hover:text-white hover:bg-white/5'
                    }`}
                >
                  {groupedData.events[id].title}
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-black ${activeEventId === id ? 'bg-primary-orange text-white' : 'bg-white/5 text-white/30'}`}>
                    {groupedData.events[id].tickets.length}
                  </span>
                </button>
              ))}
            </div>
          )}

          {currentTickets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentTickets.map((ticket) => {
                const esValida = ticket.valid;
                return (
                  <div
                    key={ticket._id}
                    className={`relative bg-surface p-6 rounded-3xl border transition-all duration-300 group overflow-hidden ${esValida ? 'border-primary-orange/20 shadow-lg shadow-orange-500/5' : 'border-white/5 opacity-60'
                      }`}
                  >
                    {esValida && (
                      <div className="absolute top-0 right-0 p-3">
                        <span className="flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-orange opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-orange"></span>
                        </span>
                      </div>
                    )}

                    <div className="space-y-3 mb-6 relative z-10">
                      <div>
                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px] mb-1">Evento</p>
                        <p className="text-lg font-bold text-white group-hover:text-primary-orange transition-colors line-clamp-1">{ticket.event?.title || 'Sin Evento'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px] mb-1">Ubicación</p>
                        <p className="text-white/70 font-medium line-clamp-1">{ticket.event?.location || 'Sin ubicación'}</p>
                      </div>
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-white/70 font-medium lowercase first-letter:uppercase">
                            {ticket.event?.date ? new Date(ticket.event.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) : 'N/D'}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${esValida ? 'bg-primary-orange/10 text-primary-orange' : 'bg-white/10 text-white/40'
                          }`}>
                          {esValida ? 'Válida' : 'Utilizada'}
                        </div>
                      </div>

                      {/* Botón Cómo llegar */}
                      {(ticket.event?.address || ticket.event?.location) && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            (ticket.event.address ? `${ticket.event.address}, ${ticket.event.city || ''}` : ticket.event.location)
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-primary-orange transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                          Cómo llegar
                        </a>
                      )}
                    </div>

                    {esValida && ticket.qrImage ? (
                      <div className="mt-4 p-4 bg-white rounded-2xl flex justify-center shadow-inner group-hover:scale-[1.02] transition-transform cursor-pointer" onClick={() => abrirQR(ticket)}>
                        <img
                          src={ticket.qrImage}
                          alt="QR del ticket"
                          className="w-32 h-32"
                        />
                      </div>
                    ) : (
                      <div className="mt-4 p-6 bg-white/5 rounded-2xl border border-white/5 text-center">
                        <p className="text-xs text-white/30 font-medium italic">
                          Entrada ya utilizada. QR no disponible.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-surface p-12 rounded-3xl border border-white/5 text-center shadow-2xl">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl opacity-40">🎫</span>
              </div>
              <p className="text-white/40 font-bold">No se encontraron entradas.</p>
              <button
                onClick={() => { setSearchTerm(''); setActiveEventId('all'); }}
                className="mt-4 text-primary-orange font-bold hover:underline"
              >
                Limpiar filtros
              </button>
            </div>
          )}
        
            </>
          ) : (
            <div className="mt-8">
              {matchTickets.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {matchTickets.map(ticket => {
                    const esValida = ticket.status === 'valid';
                    return (
                      <div key={ticket._id} className={`relative bg-surface p-6 rounded-3xl border transition-all duration-300 group overflow-hidden ${esValida ? 'border-primary-orange/20 shadow-lg shadow-orange-500/5' : 'border-white/5 opacity-60'}`}>
                         {esValida && (
                          <div className="absolute top-0 right-0 p-3">
                            <span className="flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-orange opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-orange"></span>
                            </span>
                          </div>
                        )}
                        <div className="space-y-3 mb-6 relative z-10">
                          <div>
                            <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px] mb-1">Torneo</p>
                            <p className="text-lg font-bold text-white group-hover:text-primary-orange transition-colors line-clamp-1">{ticket.tournamentId?.name || 'Torneo'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px] mb-1">Partido</p>
                            <p className="text-white/70 font-medium line-clamp-1">{ticket.matchId?.teamA?.name} vs {ticket.matchId?.teamB?.name}</p>
                          </div>
                          <div className="flex justify-between items-end">
                            <div>
                              <p className="text-white/70 font-medium lowercase first-letter:uppercase">
                                {ticket.matchId?.date ? new Date(ticket.matchId.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) : 'Fecha a definir'}
                              </p>
                              <p className="text-xs text-white/50">{ticket.matchId?.location}</p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${esValida ? 'bg-primary-orange/10 text-primary-orange' : 'bg-white/10 text-white/40'}`}>
                              {esValida ? 'Válida' : 'Utilizada'}
                            </div>
                          </div>
                        </div>
                        {esValida && ticket.qrImage ? (
                          <div className="mt-4 p-4 bg-white rounded-2xl flex justify-center shadow-inner group-hover:scale-[1.02] transition-transform cursor-pointer" onClick={() => setQrAmpliado({ src: ticket.qrImage, fileName: 'entrada_partido.png' })}>
                            <img src={ticket.qrImage} alt="QR del ticket" className="w-32 h-32" />
                          </div>
                        ) : (
                          <div className="mt-4 p-6 bg-white/5 rounded-2xl border border-white/5 text-center">
                            <p className="text-xs text-white/30 font-medium italic">Entrada ya utilizada. QR no disponible.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-surface p-12 rounded-3xl border border-white/5 text-center shadow-2xl">
                  <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl opacity-40">⚽</span>
                  </div>
                  <p className="text-white/40 font-bold">No tienes entradas para partidos.</p>
                </div>
              )}
            </div>
          )}

        </div>
      )
      }

      {/* 🆕 MODAL QR */}
      {
        qrAmpliado && (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4 backdrop-blur-md"
            onClick={() => setQrAmpliado(null)}
          >
            <div
              className="bg-surface p-8 rounded-[2rem] border border-white/10 shadow-3xl max-w-sm w-full relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient"></div>
              <h4 className="text-center text-white font-bold mb-6 tracking-tight">Tu Entrada Digital</h4>
              <div className="p-6 bg-white rounded-3xl mb-8">
                <img
                  src={qrAmpliado.src}
                  alt="QR ampliado"
                  className="w-full h-auto object-contain"
                />
              </div>

              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={descargarQR}
                  className="w-full bg-primary-orange text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-accent transition-all active:scale-95 shadow-lg shadow-orange-500/20"
                >
                  Descargar Imagen
                </button>

                {/* Botón Cómo llegar en Modal */}
                {(() => {
                  // Necesitamos la data del evento. Como qrAmpliado solo tiene src y fileName, 
                  // necesitamos buscar el ticket original o pasar la data.
                  // Simplificación: al abrirQR pasamos el ticket completo o extraemos la data antes.
                  // Mejor opción: modificar abrirQR para guardar el ticket o la info de ubicación.
                  // Por ahora, mostraré el botón si modificamos el estado qrAmpliado, pero primero debo actualizar abrirQR.
                  return qrAmpliado.locationQuery ? (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(qrAmpliado.locationQuery)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-white/5 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-white/10 transition-all text-center flex items-center justify-center gap-2"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                      Cómo llegar
                    </a>
                  ) : null;
                })()}

                <button
                  onClick={() => setQrAmpliado(null)}
                  className="w-full py-3 text-white/40 font-bold text-sm hover:text-white transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default Profile;
